import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import { nameof } from "../../../../../utils/nameof";
import { AssetInventoryCompletenessSection } from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { useMissingFieldMessages } from "../../../hooks/useMissingFieldMessages";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { describeMissingDonationField } from "../DonationsView.missingFields";
import { DonationsFormValues } from "../DonationsView.types";
import { DONATIONS_UPLOAD_AREA, fromDonationsResponse, hasAnyYes, toDonationsRequest } from "../DonationsView.utils";
import { createDonationsSchema } from "../DonationsView.validation";

export function useAssetDonations(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const moveDocuments = useAssetDocumentMoves(clientId);

  const queryKey = [ClientFileQueryKey.AssetInventoryDonations, clientId];

  const { data } = useQuery({
    queryKey,
    queryFn: () => clientsApi.getAssetInventoryDonations({ clientId }),
    enabled: !!clientId,
  });

  const uploadDocuments = async (files: File[]): Promise<File[]> => {
    if (!documentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await documentsApi.uploadAssetInventoryAssetDocument(
            { clientId, area: DONATIONS_UPLOAD_AREA.area },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [DONATIONS_UPLOAD_AREA.queryKey, clientId] });
    return failed;
  };

  const validationSchema = useMemo(() => createDonationsSchema(t), [t]);

  const initialValues = useMemo(() => fromDonationsResponse(data), [data]);

  const formik = useFormikWithDirtyForm<DonationsFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const request = toDonationsRequest(values);

      if (JSON.stringify(request) !== JSON.stringify(toDonationsRequest(initialValues))) {
        await clientsApi.saveAssetInventoryDonations({ clientId, saveDonationsRequest: request });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
        await queryClient.invalidateQueries({ queryKey });
      }

      const isUploadShown = hasAnyYes(values.answers);
      const pending = values.donationDocuments;
      let failedUploads: File[] = [];
      if (isUploadShown && pending.length > 0) {
        failedUploads = await uploadDocuments(pending);
      }
      if (pending.length > 0) {
        await helpers.setFieldValue(DONATIONS_UPLOAD_AREA.fieldName, failedUploads);
      }

      const moves = isUploadShown
        ? values.documentMoves
        : values.documentMoves.filter((move) => move.target !== DONATIONS_UPLOAD_AREA.zone);
      const failedMoves = await moveDocuments(moves);
      await helpers.setFieldValue(nameof<DonationsFormValues>("documentMoves"), failedMoves);

      if (failedUploads.length === 0 && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.donationsTab.saveSuccess"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  const missingFields = useMissingFieldMessages(
    clientId,
    AssetInventoryCompletenessSection.Donations,
    formik.values,
    describeMissingDonationField
  );

  return { formik, missingFields, onAddMove, onCancelMove };
}
